// macadamia-app/src/layout/TopBar.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTokens } from '../context/TokenContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ThemeSelector from '../components/shared/ThemeSelector';
import NotificationDropdown from '../components/shared/NotificationDropdown';
import ProfilePictureManager from './ProfilePictureManager';
import useViewport from '../hooks/useViewport';
import useNotifications from '../macadamia-core/components/hooks/useNotifications';
import { formatTokenAmount } from '../utils/tokenFormatter';

const TopBar = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { logout, user } = useAuth();
  const { balance } = useTokens();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useViewport();

  const {
    notifications,
    isLoading,
    hasMore,
    loadMore,
    markAsRead,
    totalUnreadCount,
    fetchNotifications,
  } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsDropdownOpen(false);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  // Vérifier si un chemin est actif
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const menuItems = [
    { label: 'Mon profil', to: '/mon-profil' },
    { label: 'Mes tokens', to: '/mes-tokens' },
    { label: 'Facturation', to: '/facturation' },
    // { label: 'Mon plan', to: '/choisir-plan' }, // Hidden - no business plan offers available yet
  ];

  // Style pour les liens de navigation
  const navLinkStyle = "px-3 py-2 mx-1 text-sm font-medium transition-colors duration-200 inline-flex items-center";
  const activeLinkStyle = `${navLinkStyle} text-mcdm-secondary-400 dark:text-mcdm-secondary-300 font-bold border-b-2 border-mcdm-secondary-400 dark:border-mcdm-secondary-300`;
  const inactiveLinkStyle = `${navLinkStyle} text-mcdm-third-500 dark:text-mcdm-primary-100 hover:text-mcdm-secondary-500 dark:hover:text-mcdm-secondary-400`;

  // Callback quand on ouvre le dropdown de notifications
  const handleNotificationOpen = () => {
    if (fetchNotifications) {
      fetchNotifications(1); // Charger la première page
    }
  };

  return (
    <div className="bg-mcdm-primary-200 dark:bg-mcdm-third-400 shadow fixed top-0 left-0 right-0 z-20">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to="/">
                <img
                  className="w-8 h-auto cursor-pointer"
                  src="/logos/logo-macadamia-v2.png"
                  alt="Logo Macadamia"
                />
              </Link>
            </div>
          </div>

          <div className="flex items-center">
            {/* Menu de navigation - masqué en mobile */}
            {!isMobile && (
              <div className="flex mr-4">
                <Link
                  to="/documentation"
                  className={isActive('/documentation') ? activeLinkStyle : inactiveLinkStyle}
                >
                  Documentation
                </Link>
                <Link
                  to="/dossiers"
                  className={isActive('/dossiers') ? activeLinkStyle : inactiveLinkStyle}
                >
                  Mes dossiers
                </Link>
                <Link
                  to="/jurisprudences"
                  className={isActive('/jurisprudences') ? activeLinkStyle : inactiveLinkStyle}
                >
                  Jurisprudences
                </Link>
                <Link
                  to="/hal"
                  className={isActive('/hal') ? activeLinkStyle : inactiveLinkStyle}
                >
                  HAL
                </Link>
                <Link
                  to="/damia"
                  className="px-4 py-2 mr-1 ml-3 text-sm font-medium transition-all duration-200
                            bg-mcdm-green-400 hover:bg-mcdm-green-500
                            dark:bg-mcdm-green-300 dark:hover:bg-mcdm-green-400
                            text-white dark:text-white
                            rounded-full shadow-sm hover:shadow-md
                            border border-transparent inline-flex items-center"
                >
                  Essayez Damia
                </Link>
              </div>
            )}

            <ThemeSelector />
            <NotificationDropdown
              notifications={notifications}
              isLoading={isLoading}
              hasMore={hasMore}
              loadMore={loadMore}
              markAsRead={markAsRead}
              unreadCount={totalUnreadCount}
              onOpen={handleNotificationOpen}
            />

            <div className="ml-4 relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="focus:outline-none"
                style={{ marginTop: '5px' }}
              >
                <ProfilePictureManager
                  size="md"
                  className="border border-gray-200 dark:border-gray-600 shadow-sm transition-all duration-200 
                           rounded-full ring-2 ring-transparent hover:ring-gray-300 dark:hover:ring-gray-600 
                           hover:ring-offset-2 hover:ring-offset-mcdm-primary-200 dark:hover:ring-offset-mcdm-third-400"
                />
              </button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="absolute right-0 mt-0 w-48 bg-white dark:bg-mcdm-third-400
                             rounded-lg shadow-lg shadow-inner py-1 z-20"
                  >
                    <div className="px-4 mt-2 mb-0">
                      <div className="text-sm font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100">
                        {user?.name}
                      </div>
                      <Link
                        to="/mes-tokens"
                        onClick={closeDropdown}
                        className="text-xs text-mcdm-secondary-500 dark:text-mcdm-secondary-400 hover:underline cursor-pointer"
                      >
                        {formatTokenAmount(balance)} token{balance > 1 ? 's' : ''}
                      </Link>
                    </div>
                    <hr className="my-0 mt-0 mx-2 border-gray-200 dark:border-gray-600" />

                    {/* En mobile, ajouter les liens de navigation dans le dropdown */}
                    {isMobile && (
                      <>
                        <Link
                          to="/documentation"
                          onClick={closeDropdown}
                          className="block px-4 py-2 mb-1 text-sm text-mcdm-third-500 dark:text-mcdm-primary-200
                                   hover:bg-gray-100 dark:hover:bg-mcdm-third-300 cursor-pointer rounded mx-2"
                        >
                          Documentation
                        </Link>
                        <Link
                          to="/dossiers"
                          onClick={closeDropdown}
                          className="block px-4 py-2 mb-1 text-sm text-mcdm-third-500 dark:text-mcdm-primary-200
                                   hover:bg-gray-100 dark:hover:bg-mcdm-third-300 cursor-pointer rounded mx-2"
                        >
                          Mes dossiers
                        </Link>
                        <Link
                          to="/jurisprudences"
                          onClick={closeDropdown}
                          className="block px-4 py-2 mb-1 text-sm text-mcdm-third-500 dark:text-mcdm-primary-200
                                   hover:bg-gray-100 dark:hover:bg-mcdm-third-300 cursor-pointer rounded mx-2"
                        >
                          Jurisprudences
                        </Link>
                        <Link
                          to="/hal"
                          onClick={closeDropdown}
                          className="block px-4 py-2 mb-1 text-sm text-mcdm-third-500 dark:text-mcdm-primary-200
                                   hover:bg-gray-100 dark:hover:bg-mcdm-third-300 cursor-pointer rounded mx-2"
                        >
                          HAL
                        </Link>
                        <Link
                          to="/damia"
                          onClick={closeDropdown}
                          className="block px-4 py-2 mb-1 text-sm text-mcdm-green-600 dark:text-mcdm-green-400
                                   hover:bg-gray-100 dark:hover:bg-mcdm-third-300 cursor-pointer rounded mx-2 font-medium"
                        >
                          Essayer Damia
                        </Link>
                        <hr className="my-1 mx-2 border-gray-200 dark:border-gray-600" />
                      </>
                    )}

                    {menuItems.map((item, index) => (
                      <Link
                        key={index}
                        to={item.to}
                        onClick={closeDropdown}
                        className="block px-4 py-2 mb-1 text-sm text-mcdm-third-500 dark:text-mcdm-primary-200
                                 hover:bg-gray-100 dark:hover:bg-mcdm-third-300 cursor-pointer rounded mx-2"
                      >
                        {item.label}
                      </Link>
                    ))}

                    <hr className="my-0 mx-2 border-gray-200 dark:border-gray-600" />
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 mb-1 text-sm text-red-600 dark:text-red-400
                               hover:bg-gray-100 dark:hover:bg-mcdm-third-300 hover:text-red-700
                               dark:hover:text-red-300 rounded mx-2"
                    >
                      Déconnexion
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;